import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Target, Ticket, FileText, UserSquare2,
  ListTodo, Calendar, MessageSquare, PenSquare, Inbox, Mail,
  BarChart3, Banknote, Landmark, Bot, LogOut, Search, Bell, ChevronDown,
  Shield, HeartHandshake, Sparkles, Briefcase, ShoppingCart, Megaphone,
  Sun, Moon, GraduationCap
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationsBell from "@/components/NotificationsBell";
import { Input } from "@/components/ui/input";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
      { to: "/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
      { to: "/ai", label: "AI Assistant", icon: Sparkles, id: "ai" },
    ],
  },
  {
    label: "Sales",
    items: [
      { to: "/contacts", label: "Contacts", icon: Users, id: "contacts" },
      { to: "/leads", label: "Leads Pipeline", icon: Target, id: "leads" },
      { to: "/deals", label: "Deals", icon: BarChart3, id: "deals" },
    ],
  },
  {
    label: "Support",
    items: [
      { to: "/tickets", label: "Tickets", icon: Ticket, id: "tickets" },
      { to: "/inbox", label: "Omnichannel", icon: Inbox, id: "inbox" },
      { to: "/emails", label: "Email", icon: Mail, id: "emails" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/invoices", label: "Invoices", icon: FileText, id: "invoices" },
      { to: "/projects", label: "Projects", icon: Briefcase, id: "projects" },
      { to: "/tasks", label: "Tasks", icon: ListTodo, id: "tasks" },
      { to: "/procurement", label: "Procurement", icon: ShoppingCart, id: "procurement" },
      { to: "/marketing", label: "Marketing", icon: Megaphone, id: "marketing" },
      { to: "/academy", label: "Figure Academy", icon: GraduationCap, id: "academy" },
      { to: "/calendar", label: "Calendar", icon: Calendar, id: "calendar" },
      { to: "/chat", label: "Team Chat", icon: MessageSquare, id: "chat" },
      { to: "/whiteboard", label: "Whiteboard", icon: PenSquare, id: "whiteboard" },
    ],
  },
  {
    label: "Company",
    items: [
      { to: "/staff", label: "Staff & HR", icon: UserSquare2, id: "staff" },
      { to: "/hr", label: "Attendance & Reviews", icon: HeartHandshake, id: "hr" },
      { to: "/accounting", label: "Accounting", icon: BarChart3, id: "accounting" },
      { to: "/payroll", label: "Payroll", icon: Banknote, id: "payroll" },
      { to: "/taxes", label: "Taxes", icon: Landmark, id: "taxes" },
      { to: "/automation", label: "Automation", icon: Bot, id: "automation" },
      { to: "/admin", label: "Admin Console", icon: Shield, id: "admin", adminOnly: true },
    ],
  },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#F8FAFC] dark:bg-slate-950">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-brand-navy text-white flex flex-col" data-testid="app-sidebar">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
          <img src="/assets/figuraid-logo.png" alt="Figuraid" className="w-9 h-9 object-contain bg-white/95 rounded-md p-0.5" />
          <div className="leading-tight">
            <div className="font-heading font-semibold text-white text-[15px]">FiguraidOS</div>
            <div className="text-[11px] text-slate-400">Internal Ops</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-3 mb-2">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  if (item.adminOnly && user?.role !== "admin" && user?.role !== "manager") return null;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={`nav-${item.id}`}
                      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                    >
                      <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="text-[11px] text-slate-400 px-2">
            v0.1 · &copy; 2026 Figuraid
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 flex items-center justify-between px-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-10" data-testid="app-header">
          <GlobalSearch />
          <div className="flex items-center gap-2">
            <button onClick={toggle} className="w-9 h-9 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center" data-testid="theme-toggle" title={theme === "light" ? "Switch to dark" : "Switch to light"}>
              {theme === "light" ? <Moon className="w-4 h-4 text-slate-600" /> : <Sun className="w-4 h-4 text-slate-300" />}
            </button>
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="user-menu-btn">
                  <Avatar className="w-7 h-7">
                    <AvatarImage src={user?.avatar} alt={user?.name} />
                    <AvatarFallback>{user?.name?.[0] ?? "U"}</AvatarFallback>
                  </Avatar>
                  <div className="text-left leading-tight hidden md:block">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{user?.name}</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 capitalize">{user?.role?.replace("_", " ")}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/me")} data-testid="menu-profile">
                  <UserSquare2 className="w-4 h-4 mr-2" /> My HR Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6 space-y-6 animate-fade-in" data-testid="page-container">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
